/**
 * Helpers for `?returnTo=` round trips between staff-ui pages (e.g. the submissions
 * Link contact modal → create contact → back to the submissions list).
 *
 * `returnTo` comes from the URL, so anyone can craft it. Only same-origin, app-relative
 * paths are accepted; everything else is dropped, which closes the open-redirect hole a
 * naive `navigate(searchParams.get("returnTo"))` would open.
 */

/**
 * Backslashes (browsers treat them as `/`) and control characters (browsers strip tabs and
 * newlines, so `/\t/evil.com` becomes `//evil.com`). A code-point check rather than a
 * regex because biome forbids control characters in regular expressions.
 */
function hasUnsafeCharacter(value: string): boolean {
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (char === "\\" || code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/**
 * Returns `raw` when it is a safe in-app path (`/foo?bar#baz`), otherwise null.
 *
 * Rejected: absolute URLs (`https://evil.com`), scheme-relative (`//evil.com`),
 * backslash tricks (`/\evil.com`), `javascript:` and other schemes, control
 * characters, and anything that resolves to a different origin.
 */
export function getSafeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const value = raw.trim();
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  if (hasUnsafeCharacter(value)) return null;

  // Belt and braces: let the URL parser have the final word on the origin.
  const base = "https://staff-ui.invalid";
  try {
    const resolved = new URL(value, base);
    if (resolved.origin !== base) return null;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return null;
  }
}

/** Sets (or replaces) one query parameter on an app-relative path, keeping any hash. */
export function withQueryParam(path: string, key: string, value: string): string {
  const base = "https://staff-ui.invalid";
  const url = new URL(path, base);
  url.searchParams.set(key, value);
  return `${url.pathname}${url.search}${url.hash}`;
}
